import React, { useEffect, useMemo, useState } from 'react'

import { Badge } from '@material-ui/core'
import { useSelector } from 'react-redux'

import { axiosInstance, VENDOR } from '../helpers'
import { useUser } from '../hooks'
import { Invoice } from '../models'
import { RootState } from '../store'

const countPendingRequests = (invoices: Invoice[]) =>
    invoices.filter(
        (invoice) =>
            invoice.apartment_initiated_status === 'PENDING' &&
            invoice.total_cost > 0,
    ).length

const VendorInvoiceRequestBadge: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { workspaceUser } = useUser()
    const [fetchedCount, setFetchedCount] = useState(0)

    const invoiceList = useSelector(
        (state: RootState) => state.invoice.invoiceList,
    )
    const invoiceListWorkspaceId = useSelector(
        (state: RootState) => state.invoice.invoiceListWorkspaceId,
    )

    const currentWorkspaceId = workspaceUser?.active_workspace?.id
    const isVendor = workspaceUser?.active_workspace.company_type === VENDOR

    const reduxCount = useMemo(() => {
        if (
            invoiceList == null ||
            invoiceListWorkspaceId !== currentWorkspaceId
        ) {
            return null
        }
        return countPendingRequests(invoiceList)
    }, [invoiceList, invoiceListWorkspaceId, currentWorkspaceId])

    useEffect(() => {
        if (!isVendor || currentWorkspaceId == null) {
            setFetchedCount(0)
            return
        }
        if (reduxCount != null) {
            return
        }

        let cancelled = false
        axiosInstance
            .get<Invoice[]>('invoice/', {
                params: { pending_invoice_request: true },
            })
            .then((res) => {
                if (!cancelled) {
                    setFetchedCount(countPendingRequests(res.data))
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFetchedCount(0)
                }
            })

        return () => {
            cancelled = true
        }
    }, [isVendor, currentWorkspaceId, reduxCount])

    const pendingCount = reduxCount ?? fetchedCount

    return (
        <Badge
            badgeContent={pendingCount}
            color="error"
            invisible={!pendingCount}
        >
            {children}
        </Badge>
    )
}

export default VendorInvoiceRequestBadge
